import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import { Box, IconButton, Typography } from "@mui/material";
import { type FC, memo, useMemo } from "react";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useUserSettingsContext } from "../../../contexts/Staffing/UserSettingsContext";
import { useWeekViewContext } from "../../../contexts/WeekView";
import { useNurseWeeklyOT } from "../../../hooks/useNurseWeeklyOT";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useRoleColors } from "../../../hooks/useRoleColors";
import { ActiveTimeOffRequest } from "../../../pages/SynergyModeStaffing/Cells/ActiveTimeOffRequest/ActiveTimeOffRequest";
import { INK, STATUS_BADGE } from "../../../theme/tokens";
import { getShiftDisplayDate } from "../../../utils/dateUtils";
import { WeeklyGroupingType } from "../../../utils/models";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { NurseStatusIcons } from "../../NurseStatusIcons/NurseStatusIcons";
import { useGetDepartmentTimeOffRequests } from "../../RequestTimeOff/timeOffRequestsHooks";
import type { NurseSlot } from "./useWeeklyGapView";

export const NurseCellContent: FC<{
	nurse: NurseSlot;
	onNurseClick: (nurse: NurseSlot, anchor: HTMLElement) => void;
}> = memo(({ nurse, onNurseClick }) => {
	const roleColor = useRoleColors();
	const { isMedicalDepartment, chosenDepartment } = useHospitalData();
	const { getSchedulePerson } = useWeekViewContext();
	const isOT = useNurseWeeklyOT({
		nurseId: nurse.nurseInShift.nurseId,
		referenceDate: getShiftDisplayDate(nurse.nurseInShift.startTime, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes),
		enabled: !isMedicalDepartment,
	});

	const { settings } = useUserSettingsContext();
	const groupingType = settings.weeklyGroupingType;
	const role = groupingType !== WeeklyGroupingType.Role ? getSchedulePerson(nurse.nurseInShift.nurseId)?.role : null;
	const isTimeOffRequestsEnabled = useFeatureFlag(FEATURE_FLAGS.TimeOffRequestsEnabled);
	const { data: timeOffRequests, refetch: refetchTimeOff, isFetching: isRefetchingTimeOff } = useGetDepartmentTimeOffRequests();
	const staffTimeOffRequests = useMemo(
		() => timeOffRequests?.filter((r) => r.requestingStaffId === nurse.nurseInShift.nurseId && !r.deleted) ?? [],
		[timeOffRequests, nurse.nurseInShift.nurseId],
	);

	return (
		<>
			<Box
				data-testid={`weekly-nurse-cell-${nurse.nurseInShift.nurseId}`}
				sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", width: "calc(100% - 20px)" }}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, maxWidth: "100%" }}>
					{role && (
						<InHouseTooltip title={role}>
							<Box sx={{ width: 8, height: 8, background: roleColor(role).dot, borderRadius: "50%" }} />
						</InHouseTooltip>
					)}
					<InHouseTooltip title={nurse.name}>
						<Typography sx={{ maxWidth: "100%", fontSize: "12px", color: INK.SECONDARY, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
							{nurse.name}
						</Typography>
					</InHouseTooltip>
					{!!nurse.nurseInShift.recurringShiftId && (
						<InHouseTooltip title={"Recurring shift"}>
							<RefreshOutlined sx={{ fontSize: "12px" }} />
						</InHouseTooltip>
					)}
					<NurseStatusIcons
						person={getSchedulePerson(nurse.nurseInShift.nurseId)}
						charge={nurse.nurseInShift.charge}
						trainingStatus={nurse.nurseInShift.trainingStatus}
					/>
					{isOT && <Typography sx={{ fontWeight: 600, fontSize: "10px", color: STATUS_BADGE.CORAL, flexShrink: 0 }}>OT</Typography>}
				</Box>
				{nurse.showHours && (
					<Typography sx={(theme) => ({ fontSize: "12px", color: theme.palette.primary.main, whiteSpace: "nowrap" })}>{nurse.hoursLabel}</Typography>
				)}
			</Box>
			{isTimeOffRequestsEnabled && nurse.timeOffRequest && (
				<ActiveTimeOffRequest
					timeOffRequestsOfSpecificStaff={staffTimeOffRequests}
					dateSpecificRequest={nurse.timeOffRequest}
					refetchRequests={refetchTimeOff}
					refetchDayOffRequestsLoading={isRefetchingTimeOff}
				/>
			)}
			<IconButton
				className="nurse-menu-btn"
				data-testid={`weekly-nurse-menu-btn-${nurse.nurseInShift.nurseId}`}
				size="small"
				onClick={(e) => {
					e.stopPropagation();
					onNurseClick(nurse, e.currentTarget);
				}}
				sx={{ p: 0, ml: "auto", flexShrink: 0, transition: "opacity 0.12s ease" }}
			>
				<MoreVertIcon sx={(theme) => ({ fontSize: 14, color: theme.palette.primary.main })} />
			</IconButton>
		</>
	);
});
